import { ComponentRegistry } from '@ts/utils/ComponentRegistry';

type ProductVariant = {
  id: string;
  price: number;
  savings: number;
  images: Array<{ detailUrl: string; cartUrl: string; isDefault: boolean }>;
  choices: Array<{ optionId: string; choiceId: string }>;
};

type ProductDataJson = {
  variants: ProductVariant[];
};

export interface ProductDataOptions {
  defaultVariantId?: string;
  onLoad?: (instance: ProductData) => void;
}

export class ProductData {
  private static registry = new ComponentRegistry<ProductData>('ProductData');
  private data: ProductDataJson | null = null;
  private activeVariant: ProductVariant | null = null;
  private defaultVariantId: string | null = null;
  private onLoadCallback?: (instance: ProductData) => void;

  private constructor(
    private dataPath: string,
    options?: ProductDataOptions
  ) {
    if (options?.defaultVariantId) {
      this.defaultVariantId = options.defaultVariantId;
    }
    if (options?.onLoad) {
      this.onLoadCallback = options.onLoad;
    }
  }

  public static async create(
    dataPath: string,
    options?: ProductDataOptions
  ): Promise<ProductData> {
    const instance = new ProductData(dataPath, options);
    await instance.loadData();
    if (instance.defaultVariantId && instance.data) {
      instance.setActiveVariant(instance.defaultVariantId);
    }
    if (instance.onLoadCallback) {
      instance.onLoadCallback(instance);
    }
    return instance;
  }

  private async loadData() {
    const response = await fetch(this.dataPath);
    this.data = await response.json();
  }

  public setActiveVariant(variantId: string) {
    if (!this.data) return;
    this.activeVariant = this.data.variants.find(v => v.id === variantId) || null;
  }

  public getPrice(): number | null {
    return this.activeVariant ? this.activeVariant.price : null;
  }

  public getSavings(): number | null {
    return this.activeVariant ? this.activeVariant.savings : null;
  }

  public getImages(): Array<{ detailUrl: string; cartUrl: string; isDefault: boolean }> {
    return this.activeVariant ? this.activeVariant.images : [];
  }

  /**
   * Returns all available choices for the given optionId, given the current active variant's selections.
   * Only choices that are valid in combination with the current selection are returned.
   */
  public getAvailableChoices(optionId: string): Array<{ choiceId: string; value: string }> {
    if (!this.data) return [];
    // Gather current selections from the active variant, except for the optionId in question
    const selectedChoices: Record<string, string> = {};
    if (this.activeVariant) {
      for (const choice of this.activeVariant.choices) {
        if (choice.optionId !== optionId) {
          selectedChoices[choice.optionId] = choice.choiceId;
        }
      }
    }
    // For each variant, if it matches the current selections (except optionId), collect its choice for optionId
    const choicesSet = new Set<string>();
    this.data.variants.forEach(variant => {
      const matches = Object.entries(selectedChoices).every(([optId, chId]) =>
        variant.choices.some(c => c.optionId === optId && c.choiceId === chId)
      );
      if (matches) {
        const found = variant.choices.find(c => c.optionId === optionId);
        if (found) choicesSet.add(found.choiceId);
      }
    });
    return Array.from(choicesSet).map(choiceId => ({ choiceId, value: choiceId }));
  }
}